import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@cibli/shared';
import { Camera, RotateCw } from 'lucide-react';
import React, { ChangeEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LOCAL_STORAGE_DEVICE_ID = 'camera-device-id';
const LOCAL_STORAGE_ROTATION = 'camera-rotation';
const PHOTO_COUNTDOWN_SECONDS = 5;

interface CameraDialogProps {
  open: boolean;
  showSettings?: boolean;
  onOpenChange: (open: boolean) => void;
  onPhotoCaptured: (imageBase64: string) => void;
}

const CameraDialog: React.FC<CameraDialogProps> = ({
  open,
  showSettings,
  onOpenChange,
  onPhotoCaptured,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(
    () => localStorage.getItem(LOCAL_STORAGE_DEVICE_ID) || undefined,
  );
  const [rotation, setRotation] = useState<number>(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_ROTATION);
    return stored ? parseInt(stored, 10) : 0;
  });

  // Initialize camera when dialog is opened
  useEffect(() => {
    if (!open) return;
    startCamera(deviceId);
    getDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    setDevices(devices.filter(device => device.kind === 'videoinput'));
  };

  const startCamera = async (id: string | undefined) => {
    if (stream) stopCamera();

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: id,
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
      });
      setStream(mediaStream);

      // Detect deviceId
      const deviceId = mediaStream.getVideoTracks()[0]?.getSettings().deviceId;
      if (deviceId) {
        setDeviceId(deviceId);
      }

      // Apply stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      onOpenChange(false);
    }
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const handleRotate = () => {
    setRotation(r => {
      const next = (r + 90) % 360;
      localStorage.setItem(LOCAL_STORAGE_ROTATION, next.toString());
      return next;
    });
  };

  const capturePhoto = useCallback(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      const width = video.videoWidth;
      const height = video.videoHeight;
      const canvas = document.createElement('canvas');
      if (rotation === 90 || rotation === 270) {
        canvas.width = height;
        canvas.height = width;
      } else {
        canvas.width = width;
        canvas.height = height;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.save();
      // Move to center for rotation
      if (rotation === 90) {
        ctx.translate(height, 0);
        ctx.rotate((90 * Math.PI) / 180);
      } else if (rotation === 180) {
        ctx.translate(width, height);
        ctx.rotate(Math.PI);
      } else if (rotation === 270) {
        ctx.translate(0, width);
        ctx.rotate((270 * Math.PI) / 180);
      }
      ctx.drawImage(video, 0, 0, width, height);
      ctx.restore();
      const base64Image = canvas.toDataURL('image/webp', 1);
      onPhotoCaptured(base64Image);
      stopCamera();
      onOpenChange(false);
    }
  }, [rotation, onPhotoCaptured, stopCamera, onOpenChange]);

  const capturePhotoRef = useRef(capturePhoto);
  capturePhotoRef.current = capturePhoto;

  const startCountdown = () => setCountdown(PHOTO_COUNTDOWN_SECONDS);
  const cancelCountdown = () => setCountdown(null);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          if (prev === 1) {
            capturePhotoRef.current();
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [countdown]);

  const handleCameraChange: ChangeEventHandler<HTMLSelectElement> = event => {
    const deviceId = event.target.value;
    setDeviceId(deviceId);
    localStorage.setItem(LOCAL_STORAGE_DEVICE_ID, deviceId);
    startCamera(deviceId);
  };

  // Handle dialog close
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      setCountdown(null);
      stopCamera();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="z-[999999]" overlayClassName="z-[999998]">
        <DialogHeader>
          <DialogTitle>{t('Forms.PersonalInfo.photoDialog.title')}</DialogTitle>
          <DialogDescription>{t('Forms.PersonalInfo.photoDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <div className="w-full aspect-square flex items-center justify-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="rounded-md max-h-full max-w-full"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          </div>

          {showSettings && (
            <div className="flex items-center gap-3 w-full">
              <Camera className="text-gray-600 text-xl" />
              <select
                value={deviceId}
                className="flex-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 py-2"
                onClick={getDevices}
                onChange={handleCameraChange}
              >
                {devices.map(({ deviceId, label }) => (
                  <option key={deviceId} value={deviceId}>
                    {label || 'Camera'}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="secondary"
                onClick={handleRotate}
                title={t('Forms.PersonalInfo.photoDialog.rotate')}
              >
                <RotateCw className="w-5 h-5" />
                <span className="ml-1 text-xs text-gray-600">{rotation}&deg;</span>
              </Button>
            </div>
          )}

          <div className="min-h-[3rem] flex items-center justify-center">
            {countdown !== null && (
              <span className="text-4xl font-bold tabular-nums text-primary">{countdown}</span>
            )}
          </div>

          <div className="flex justify-between w-full">
            <Button
              type="button"
              variant="secondary"
              disabled={!stream}
              onClick={() => handleDialogChange(false)}
            >
              {t('common.cancel')}
            </Button>
            {countdown !== null ? (
              <Button
                type="button"
                variant="outline"
                onClick={cancelCountdown}
                className="border-primary text-primary hover:bg-primary/10"
              >
                {t('common.cancel')}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={startCountdown}
                className="bg-primary text-white hover:bg-primary/90"
                disabled={!stream}
              >
                {t('Forms.PersonalInfo.photoDialog.takePhoto')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CameraDialog;
